/* eslint-disable jsx-a11y/role-supports-aria-props */
import React from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';

/**
 * RadioButton control with no surrounding label and error/validation message
 * functionality for React.
 */
export default class RadioButton extends React.Component {
  static propTypes = {
    /** Name for the RadioGroup. */
    name: PropTypes.string,
    onBlur: PropTypes.func,
    /** standard synthetic event */
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    /** Callback is called when user interacts with RadioGroup. Selected 'value' is passed to first argument. */
    onUpdate: PropTypes.func,
    /** RadioButton option information. Must contain: name, value.
     * May contain: checked, disabled, and focused. */
    option: PropTypes.object,
    /** Prop for altering input display when a validation or other error occurs. */
    hasError: PropTypes.bool,
    id: PropTypes.string,
    /** RadioGroup will display RadioButton instances in a row instead of block (vertically). */
    inline: PropTypes.bool,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** required prop will set accessibility-related input attributes */
    required: PropTypes.bool,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    theme: PropTypes.object
  };

  static defaultProps = {
    hasError: false,
    inline: false,
    isRtl: false,
    onChange: () => {},
    onBlur: () => {},
    onFocus: () => {},
    required: false
  };

  state = { isFocused: false };

  componentDidMount() {
    if (this.radiobutton && this.props.option.focused) {
      this.radiobutton.focus();
    }
  }

  onClick = event => {
    event.preventDefault();
    if (!this.props.option.disabled) {
      this.radiobutton.focus();
      if (this.props.onChange) {
        this.props.onChange(event);
      }
      return (
        this.props.onUpdate &&
        this.props.onUpdate(this.props.option.value, this.props.option)
      );
    }
  };

  onBlur = event => {
    this.setState({ isFocused: false });
    this.props.onBlur(event);
  };

  onFocus = event => {
    this.setState({ isFocused: true });
    this.props.onFocus(event);
  };

  render() {
    const {
      id,
      hasError,
      name,
      onUpdate,
      onChange,
      option,
      required,
      testID,
      inline,
      isRtl,
      theme,
      ...rest
    } = this.props;

    const isChecked = option.checked || false;

    return (
      <li
        css={getListItemStyles(inline)}
        onClick={this.onClick}
        onKeyPress={this.onClick}
        role="radio"
        aria-checked={isChecked}
        aria-disabled={option.disabled}
        tabIndex={-1}
      >
        <input
          {...rest}
          ref={c => {
            this.radiobutton = c;
          }}
          aria-hidden
          css={getInputStyles()}
          id={id}
          name={name}
          type="radio"
          value={option.value}
          onBlur={this.onBlur}
          onChange={onChange}
          onFocus={this.onFocus}
          checked={isChecked}
          disabled={option.disabled}
          required={required}
          {...(testID && injectTestId(`${testID}__${option.value}`))}
        />
        <label
          htmlFor={id}
          id={id + '_label'}
          css={[
            getLabelStyles(theme, option.disabled, isRtl, inline),
            getLabelStylesChecked(theme, isChecked, hasError, isRtl),
            getLabelStylesFocused(theme, this.state.isFocused, hasError),
            getLabelStylesError(theme, hasError),
            getLabelStylesDisabled(theme, option.disabled),
            getLabelStylesDisabledChecked(theme, isChecked, option.disabled)
          ]}
        >
          {option.label || option.value}
        </label>
      </li>
    );
  }
}

function getListItemStyles(inline) {
  return {
    position: 'relative',
    padding: '0.5rem 0',
    outline: 'none',
    display: inline ? 'inline-block' : 'inherit'
  };
}

function carinaRadioBase() {
  return {
    borderRadius: '50%',
    boxSizing: 'border-box',
    content: '""',
    display: 'block',
    position: 'absolute'
  };
}

function getInputStyles() {
  return {
    position: 'absolute',
    left: '-9999px'
  };
}

function getLabelStyles(theme, disabled, isRtl, inline) {
  return {
    color: disabled ? theme.font.color.disabled : theme.font.color.base,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-block',
    fontSize: theme.font.base.size.s,
    [isRtl ? 'marginRight' : 'marginLeft']: '1rem',
    paddingLeft: '0.5rem',
    paddingRight: inline ? '1rem' : '0.5rem',
    position: 'relative',
    '&::before': {
      ...carinaRadioBase(),
      fontSize: '1rem',
      backgroundColor: disabled
        ? theme.backgroundColor.surface
        : theme.backgroundColor.base,
      border: `${theme.size.border.base}px solid ${theme.borderColor.base}`,
      [isRtl ? 'right' : 'left']: '-1rem',
      top: 0,
      height: '1rem',
      width: '1rem'
    }
  };
}

function getLabelStylesChecked(theme, checked, hasError, isRtl) {
  if (!checked) return {};

  return {
    '&::before': {
      background: hasError
        ? theme.backgroundColor.danger.fill.base
        : theme.backgroundColor.interactive.fill.base,
      border: `${theme.size.border.base}px solid ${theme.borderColor.interactive.base}`
    },
    '&::after': {
      ...carinaRadioBase(),
      fontSize: '1rem',
      background: theme.backgroundColor.base,
      [isRtl ? 'right' : 'left']: '-11px',
      top: '5px',
      height: '0.375rem',
      width: '0.375rem'
    }
  };
}

function getLabelStylesFocused(theme, isFocused, hasError) {
  if (!isFocused) return {};

  return {
    '&::before': {
      boxShadow: hasError
        ? `0 0 0 ${theme.size.border.focus}px ${theme.backgroundColor.danger.fill.focus}`
        : `0 0 0 ${theme.size.border.focus}px ${theme.backgroundColor.interactive.fill.focus}`
    }
  };
}

function getLabelStylesDisabled(theme, disabled) {
  if (!disabled) return {};

  return {
    '&::before': {
      border: `${theme.size.border.base}px solid ${theme.borderColor.disabled}`
    }
  };
}

function getLabelStylesDisabledChecked(theme, checked, disabled) {
  if (!disabled || !checked) return {};

  return {
    '&::before': {
      border: `${theme.size.border.base}px solid ${theme.borderColor.disabled}`,
      background: theme.backgroundColor.disabled
    }
  };
}

function getLabelStylesError(theme, hasError) {
  if (!hasError) return {};

  return {
    '&::before': {
      border: `${theme.size.border.base}px solid ${theme.backgroundColor.danger.fill.base}`
    }
  };
}
